import { Matrix, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import * as basket from "../eaiws/basket";
import { GroupElement, ResourceCache, ElementChangedFlags } from "../core/mdl";
import { rotateVector3, VECTOR3_Z } from "../core/utils/MathUtils";
import { setLocalMatrix, updateFrozenWorldMatrix } from "../core/utils/MeshUtils";
import { CameraMode } from "../core/view/CameraControl";
import { ListenableEvent, Log } from "../utils";
import { forEachParallel } from "../utils/async";
import { ERR_INVALID_ARGUMENT, ERR_INVALID_STATE, newError } from "../utils/error";
import { degToRad } from "../utils/math";
import { isNullOrEmpty } from "../utils/string";
import { GfjLoader } from "./io/GfjLoader";
import { AppModule } from "./AppModule";
import { ArticleImport } from "./ArticleImport";
import { DefaultPlaceholderCreator } from "./PlaceholderCreator";
import { MainArticleElement } from "./MainArticleElement";
import { UserArticleElement } from "./UserArticleElement";
export class ArticleChangedEventData {
    constructor(pArticle, pWholeComposite) {
        this.article = pArticle;
        this.wholeComposite = pWholeComposite;
    }
}
export class ArticleManager {
    get app() {
        return (this.mApp);
    }
    get session() {
        return (this.mSession);
    }
    get maxGfjTextureSize() {
        return (this.mMaxGfjTextureSize);
    }
    /**
     * The currently active pricing procedure.
     */
    get pricingProcedure() {
        return (this.mPricingProcedure);
    }
    constructor(pApp, pEaiwsSession) {
        this.eventArticleChanged = new ListenableEvent(this);
        /**
         * Will be triggered if an article was added to the model.
         */
        this.eventArticleAdded = new ListenableEvent(this);
        /**
         * Will be triggered if an article was removed/deleted from the model.
         */
        this.eventArticleRemoved = new ListenableEvent(this);
        /**
         * Will be triggered if an article instance (basket id) was restored.
         */
        this.eventArticleInstanceRestored = new ListenableEvent(this);
        /**
         * Will be triggered for every error during execution of OAP actions.
         */
        this.eventOapError = new ListenableEvent(this);
        this.mApp = pApp;
        this.mSession = pEaiwsSession;
        this.mCatalogImageCache = new ResourceCache();
        this.mPricingProcedure = null;
        this.fatherItemId = null;
        this.includeCalculationErrorsInInconsistencies = true;
        this.mInstancedArticles = new Array();
        this.mGfjLoader = new GfjLoader(pApp.model);
        this.mGfjOptions = new Array();
        this.mGfjOptions.push("format=GFJ");
        this.mGfjOptions.push("hideSubArticles=false");
        this.mGfjOptions.push("basketIds=false");
        //this.mGfjOptions.push("geometryExtensions=.gltf");
        this.mGfjOptions.push("geometryExtensions=.glb");
        this.mGfjOptions.push("maxImageSize=1024");
        this.mMaxGfjTextureSize = 1024;
        this.placeholderCreator = new DefaultPlaceholderCreator(this);
        this.mApp.registerModule(new AppModule(this));
        this.mApp.model.registerElementLoader(MainArticleElement.CLASS_ID, MainArticleElement._LoadArticleElement);
        this.mApp.model.registerElementLoader(UserArticleElement.CLASS_ID, UserArticleElement._LoadArticleElement);
    }
    /**
     * Has to be called for every eaiws session to enable new OAP features.
     * If it is not called the EAIWS will run in compatibility mode.
     */
    initializeSession(pOptions) {
        if (!this.mSession.isValid) {
            throw newError(ERR_INVALID_STATE, "eaiws session is not valid");
        }
        const tCapabilities = ["MultiActionChoice"];
        if (pOptions != null && pOptions.oapPlanningMode === true)
            tCapabilities.push("PlanningMode");
        return (this.mSession.basket.oapSetClientCapabilities(tCapabilities));
    }
    /**
     * Inserts a new article into the EAIWS session and creates a MainArticleElement.
     * Note: The MainArticleElement will not be added to the ModelManager here. This should be done afterwards.
     */
    insertArticle(pArticle, pOptions) {
        if (pArticle == null)
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        let tInsertInfo;
        if (pArticle instanceof basket.InsertInfo) {
            tInsertInfo = pArticle;
        }
        else {
            tInsertInfo = new basket.InsertInfo();
            tInsertInfo.articlePackageId = pArticle.articlePackageId;
            tInsertInfo.baseArticleNumber = pArticle.baseArticleNumber;
            tInsertInfo.catalogId = pArticle.catalogId;
            tInsertInfo.catalogPackageId = pArticle.catalogPackageId;
            if (pArticle.varCodeType === "OFML")
                tInsertInfo.ofmlVariantCode = pArticle.variantCode;
            else
                tInsertInfo.variantCode = pArticle.variantCode;
        }
        const tPromise = this.mSession.basket.insertOFMLArticle(this.fatherItemId, null, tInsertInfo, pOptions).then((pId) => {
            return (this.createArticleById(pId));
        });
        return (tPromise);
    }
    /**
     * Inserts pArticle as a sub article of pMainArticle and returns the basket id of the new sub article.
     */
    async insertSubArticle(pMainArticle, pArticle, pOptions) {
        if (pArticle == null || pMainArticle == null)
            throw new Error(ERR_INVALID_ARGUMENT);
        await pMainArticle.ensureIsInstanced();
        if (pMainArticle.basketId == null)
            throw newError(ERR_INVALID_STATE, "basketId is null");
        const tInsertInfo = new basket.InsertInfo();
        tInsertInfo.articlePackageId = pArticle.articlePackageId;
        tInsertInfo.baseArticleNumber = pArticle.baseArticleNumber;
        tInsertInfo.catalogId = pArticle.catalogId;
        tInsertInfo.catalogPackageId = pArticle.catalogPackageId;
        const tInsertOptions = pOptions !== null && pOptions !== void 0 ? pOptions : new basket.InsertArticleOptions();
        if (tInsertOptions.attachmentMode == null) {
            tInsertOptions.attachmentMode = "Composite";
        }
        const tId = await this.mSession.basket.insertOFMLArticle(pMainArticle.basketId, null, tInsertInfo, tInsertOptions);
        if (isNullOrEmpty(tId)) {
            throw new Error("Failed to create sub article " + pArticle.baseArticleNumber);
        }
        else {
            await pMainArticle.updateGeometry();
            //article data of main article may have changed
            pMainArticle.notifyArticleChanged(pMainArticle, false);
            return (tId);
        }
    }
    /**
     * Creates a MainArticleElement for the given basket id which should belong to an already
     * existing main article in the EAIWS session.
     * Note: The MainArticleElement will not be added to the ModelManager here. This should be done afterwards.
     * @param pBasketId The id of the article to load.
     * @param pLoadTransformations If true the transformations will be loaded. (Default: false)
     * @param pBasketItems If the basket.BasketItems of the articles are already known. They can be passed here to speed up the operation.
     * The list of items has to be complete. It has to conatin all main articles and all of its sub articles.
     * Further, the geometryId and setArticleId (if the item is part of a set) of each BasketItem should be available.
     */
    createArticleById(pBasketId, pLoadTransformations, pBasketItems) {
        const tPromise = this.createArticlesById([pBasketId], pLoadTransformations, pBasketItems).then((pArticles) => {
            if (pArticles.length < 1)
                return (Promise.reject("Failed to create article " + pBasketId));
            else
                return (Promise.resolve(pArticles[0]));
        });
        return (tPromise);
    }
    /**
     * Creates MainArticleElements for the given basket ids which should belong to already
     * existing main articles in the EAIWS session.
     * Note: The MainArticleElements will not be added to the ModelManager here. This should be done afterwards.
     * @param pBasketIds The ids of the articles to load.
     * @param pLoadTransformations If true the transformations will be loaded. (default: false)
     * @param pBasketItems If the basket.BasketItems of the articles are already known. They can be passed here to speed up the operation.
     * The list of items has to be complete. It has to conatin all main articles and all of its sub articles.
     * Further, the geometryId and setArticleId (if the item is part of a set) of each BasketItem should be available.
     * @param pGfjUrls Map from basketId to gfj url. Can be used to speed up insertion in case the urls are already known.
     */
    async createArticlesById(pBasketIds, pLoadTransformations, pBasketItems, pGfjUrls) {
        if (pBasketIds.length <= 0)
            return ([]);
        //get items
        let tAllItems = pBasketItems;
        if (tAllItems == null) {
            const tOptions = new basket.GetAllItemsOptions();
            tOptions.wholeComposite = true;
            tOptions.geometryIds = true;
            tOptions.setArticleIds = true;
            tAllItems = await this.mSession.basket.getAllItems(pBasketIds, tOptions);
            if (tAllItems.length < 1)
                throw new Error("Failed to get items.");
        }
        //build item map
        const tItemMap = new Map();
        for (const tItem of tAllItems)
            tItemMap.set(tItem.itemId, tItem);
        //get item properties
        let tAllProps = null;
        let tPropsMap = null;
        if (pLoadTransformations != null && pLoadTransformations) {
            const tPropsOptions = this._GetDefaultItemPropsOptions();
            tPropsOptions.wholeComposite = true;
            tAllProps = await this.mSession.basket.getItemProperties(pBasketIds, tPropsOptions);
            if (tAllProps.length < 1)
                throw new Error("Failed to get ItemProperties.");
            tPropsMap = new Map();
            for (const tProps of tAllProps)
                tPropsMap.set(tProps.itemId, tProps);
        }
        //create articles
        const tArticles = new Array();
        for (const tId of pBasketIds) {
            const tItem = tItemMap.get(tId);
            if (tItem == null) {
                Log.warn("Failed to get item for: " + tId);
                continue;
            }
            if (tItem.itemType === "Undefined" ||
                tItem.itemType === "Folder" ||
                tItem.itemType === "Text" ||
                tItem.itemType === "SetArticle") {
                Log.warn("Item " + tId + " is not an article.");
                continue;
            }
            let tArticle;
            if (tItem.itemType === "UserArticle")
                tArticle = await this._createUserArticleElement(tId, tItemMap, tPropsMap);
            else {
                const tGfjUrl = pGfjUrls != null ? pGfjUrls.get(tId) : undefined;
                tArticle = await this._createArticleElement(tId, tItemMap, tPropsMap, tGfjUrl);
            }
            if (pLoadTransformations && tAllProps != null && tPropsMap != null) {
                const tProps = tPropsMap.get(tId);
                if (tProps == null) {
                    Log.warn("Failed to get item properties for: " + tId);
                }
                if (tProps != null && tProps.composableGeometry != null) {
                    tArticle._loadEaiwsTransform(tProps.composableGeometry);
                }
            }
            tArticles.push(tArticle);
        }
        return (tArticles);
    }
    setGfjLayerControl(pLayerControl) {
        if (this.mGfjOptions[this.mGfjOptions.length - 1].indexOf("layerControl") !== -1) {
            if (isNullOrEmpty(pLayerControl))
                this.mGfjOptions.pop();
            else
                this.mGfjOptions[this.mGfjOptions.length - 1] = "layerControl=" + pLayerControl;
        }
        else {
            if (!isNullOrEmpty(pLayerControl))
                this.mGfjOptions.push("layerControl=" + pLayerControl);
        }
    }
    setGfjBasketIdsEnabled(pEnabled) {
        for (let i = 0; i < this.mGfjOptions.length; ++i) {
            if (this.mGfjOptions[i].startsWith("basketIds=")) {
                this.mGfjOptions[i] = "basketIds=" + (pEnabled ? "true" : "false");
                break;
            }
        }
    }
    setGfjMaxTextureSize(pMaxSize) {
        this.mMaxGfjTextureSize = pMaxSize;
        for (let i = 0; i < this.mGfjOptions.length; ++i) {
            if (this.mGfjOptions[i].startsWith("maxImageSize=")) {
                this.mGfjOptions[i] = "maxImageSize=" + this.mMaxGfjTextureSize;
                break;
            }
        }
    }
    synchronizeSession(pSaveArticleTransformations) {
        const tArticlesToDelete = new Array();
        const tNewInstanceArray = new Array();
        const tArticles = this.getAllMainArticles();
        //instantiate all articles
        let tPromise = this.ensureArticlesAreInstanced(tArticles);
        //save the transformations
        if (pSaveArticleTransformations) {
            tPromise = tPromise.then(() => {
                return (this.saveArticleTransformations(tArticles));
            });
        }
        tPromise = tPromise.then(() => {
            //collect articles which must be deleted from the session
            for (const tArticle of this.mInstancedArticles) {
                if (tArticle.isInScene()) {
                    tNewInstanceArray.push(tArticle);
                }
                else {
                    tArticlesToDelete.push(tArticle);
                }
            }
            this.mInstancedArticles = tNewInstanceArray;
            return (this.deleteArticlesFromSession(tArticlesToDelete));
        });
        return (tPromise);
    }
    /**
     * Imports articles from the current eaiws session.
     */
    async importFromSession(pOptions) {
        //load articles, get top folder id
        const tTopFolder = await this.mSession.basket.getTopFolderId();
        //get items
        const tOptions = new basket.GetAllItemsOptions();
        tOptions.subItems = true;
        tOptions.wholeComposite = true;
        tOptions.geometryIds = true;
        tOptions.setArticleIds = true;
        const tPropsOptions = this._GetDefaultItemPropsOptions();
        tPropsOptions.subItems = true;
        tPropsOptions.wholeComposite = true;
        const tIds = [tTopFolder];
        const tItems = await this.mSession.basket.getAllItems(tIds, tOptions);
        const tProps = await this.mSession.basket.getItemProperties(tIds, tPropsOptions);
        //load items
        await ArticleImport.Import(this, tItems, tProps, true, pOptions);
    }
    /**
     * Imports articles from the given obx.
     * Note: The loaded articles will not be added to the ModelManager here. This should be done afterwards.
     */
    async importObx(pObxData, pOptions) {
        if (pObxData == null)
            throw new Error(ERR_INVALID_ARGUMENT);
        //upload obx
        const tBlob = (pObxData instanceof ArrayBuffer) ? new Blob([pObxData]) : pObxData;
        const tUrl = await this.mSession.uploadFile("CutBuffer", tBlob);
        if (isNullOrEmpty(tUrl))
            throw new Error("Failed to upload obx.");
        //paste obx
        const tPastedIds = await this.mSession.basket.paste(this.fatherItemId, null, tUrl);
        if (tPastedIds == null || tPastedIds.length === 0)
            return ([]);
        //get items
        const tOptions = new basket.GetAllItemsOptions();
        tOptions.subItems = true;
        tOptions.wholeComposite = true;
        tOptions.geometryIds = true;
        tOptions.setArticleIds = true;
        const tPropsOptions = this._GetDefaultItemPropsOptions();
        tPropsOptions.subItems = true;
        tPropsOptions.wholeComposite = true;
        const tItems = await this.mSession.basket.getAllItems(tPastedIds, tOptions);
        const tProps = await this.mSession.basket.getItemProperties(tPastedIds, tPropsOptions);
        //load articles
        const tArticles = await ArticleImport.Import(this, tItems, tProps, false, pOptions);
        //delete uploaded obx
        await this.mSession.deleteFile(tUrl);
        return (tArticles);
    }
    async exportObx(pArticles) {
        if (pArticles == null)
            throw new Error(ERR_INVALID_ARGUMENT);
        await this.ensureArticlesAreInstanced(pArticles);
        await this.saveArticleTransformations(pArticles);
        //collect basketIds
        const tBasketIds = new Array();
        for (const tArticle of pArticles) {
            if (tArticle.basketId != null)
                tBasketIds.push(tArticle.basketId);
        }
        //export obx
        const tObxUrl = await this.mSession.basket.copy(tBasketIds, null, null);
        return (tObxUrl);
    }
    saveToObk(pSaveArticleTransformations) {
        const tPromise = this.synchronizeSession(pSaveArticleTransformations).then(() => {
            return (this.mSession.session.saveSession(null));
        });
        return (tPromise);
    }
    /**
     * Returns a list of all main article which are currently part of the scene.
     */
    getAllMainArticles() {
        const tArticles = new Array();
        for (const tElement of this.mApp.model.elements) {
            if (tElement instanceof MainArticleElement)
                tArticles.push(tElement);
            if (tElement instanceof GroupElement)
                this.collectMainArticlesInGroup(tElement, tArticles);
        }
        return (tArticles);
    }
    /**
     * Returns the article element with the given id.
     */
    getArticleById(pBasketId) {
        for (const tArticle of this.mInstancedArticles) {
            if (tArticle.isInScene()) {
                if (tArticle.basketId === pBasketId)
                    return (tArticle);
                else {
                    const tSub = tArticle.getSubArticleById(pBasketId);
                    if (tSub != null)
                        return (tSub);
                }
            }
        }
        return (null);
    }
    /**
     * Set active pricing procedure.
     */
    setPricingProcedure(pPricingProcedure) {
        if (this.mPricingProcedure != pPricingProcedure) {
            this.mPricingProcedure = pPricingProcedure;
            this.resetCachedArticleInfos(true);
        }
    }
    /**
     * Resets cached ArticleData and ItemProperties for all existing articles.
     */
    resetCachedArticleInfos(pNotifyPropertyChange) {
        for (const tArticle of this.mInstancedArticles)
            tArticle.notifyArticleChanged(tArticle, true);
        if (pNotifyPropertyChange) {
            this.app.model.selectionProperties.beginPropertyChange();
            for (const tArticle of this.mInstancedArticles) {
                tArticle.notifyChange(ElementChangedFlags.Properties);
                const tSubArticles = tArticle.getSubArticles(false);
                for (const tSubArticle of tSubArticles)
                    tSubArticle.notifyChange(ElementChangedFlags.Properties);
            }
            this.app.model.selectionProperties.endPropertyChange();
        }
    }
    createArticleScreenshot(pArticle, pOptions, pAsBlob) {
        if (pArticle == null || pArticle.node == null || pOptions == null)
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        let tOldTransform = null;
        //get options
        const tWidth = Math.round(pOptions.width);
        const tHeight = Math.round(pOptions.height);
        const tMargin = pOptions.margin != null ? pOptions.margin : 0.05;
        const tOrtho = pOptions.orthographic != null ? pOptions.orthographic : false;
        const tHideSubArticles = pOptions.hideSubArticles != null ? pOptions.hideSubArticles : false;
        const tCameraAlpha = pOptions.cameraAlpha != null ? pOptions.cameraAlpha : -15.0;
        const tCameraBeta = pOptions.cameraBeta != null ? pOptions.cameraBeta : 20.0;
        const tCameraFov = pOptions.cameraFov != null ? pOptions.cameraFov : 30.0;
        //get meshes to render
        const tNodes = pArticle.getNodes(!tHideSubArticles);
        let tCameraState;
        const tView = this.mApp.viewer.view;
        //before render callback
        const beforeRender = () => {
            tCameraState = tView.cameraControl.serialize();
            if (pOptions.onBeforeRender != null)
                pOptions.onBeforeRender();
            //move article to origin
            tOldTransform = pArticle.transform.clone();
            if (pArticle.node.parent != null && pArticle.node.parent instanceof TransformNode) {
                const tTransform = pArticle.node.parent.getWorldMatrix();
                setLocalMatrix(pArticle.node, Matrix.Invert(tTransform));
            }
            else
                setLocalMatrix(pArticle.node, Matrix.Identity());
            for (const tNode of tNodes) { //update world matrix
                if (tNode.isWorldMatrixFrozen)
                    updateFrozenWorldMatrix(tNode, false);
                else
                    tNode.computeWorldMatrix(true);
            }
            //setup view
            if (tOrtho)
                tView.cameraControl.cameraMode = CameraMode.Orthographic;
            else {
                tView.cameraControl.cameraMode = CameraMode.Perspective;
                tView.cameraControl.setFov(degToRad(tCameraFov));
            }
            const tQuat = Quaternion.RotationYawPitchRoll(degToRad(-tCameraAlpha), degToRad(tCameraBeta), 0.0);
            tView.cameraControl.setDirection(rotateVector3(VECTOR3_Z, tQuat));
            tView.cameraControl.zoomToFitNodes(tNodes, { margin: tMargin });
        };
        //after render callback
        const afterRender = () => {
            //reset transform
            if (tOldTransform != null) {
                setLocalMatrix(pArticle.node, tOldTransform);
                for (const tNode of tNodes) { //update world matrix
                    if (tNode.isWorldMatrixFrozen)
                        updateFrozenWorldMatrix(tNode, false);
                    else
                        tNode.computeWorldMatrix(true);
                }
            }
            if (pOptions.onAfterRender != null)
                pOptions.onAfterRender();
            if (tCameraState != null) {
                tView.cameraControl.deserialize(tCameraState);
            }
        };
        const tMeshes = new Array();
        for (const tNode of tNodes) {
            if (tNode instanceof AbstractMesh)
                tMeshes.push(tNode);
        }
        //build options
        const tOptions = Object.assign({}, pOptions);
        tOptions.width = tWidth;
        tOptions.height = tHeight;
        tOptions.meshesToRender = tMeshes;
        tOptions.onBeforeRender = beforeRender;
        tOptions.onAfterRender = afterRender;
        const tPromise = this.mApp.viewer.createScreenshot(tOptions, pAsBlob);
        return (tPromise);
    }
    /** @internal */
    _clear() {
        this.mInstancedArticles = new Array();
        this.mCatalogImageCache.clear();
    }
    /** @internal */
    _registerInstancedArticle(pArticle) {
        this.mInstancedArticles.push(pArticle);
    }
    /** @internal */
    _notifyArticleChanged(pArticle, pWholeComposite) {
        if (pArticle != null && this.eventArticleChanged.hasListeners)
            this.eventArticleChanged.trigger(new ArticleChangedEventData(pArticle, pWholeComposite));
    }
    /** @internal */
    async _importGfj(pBasketId, pItemCache, pGfjUrl) {
        if (isNullOrEmpty(pBasketId))
            return (null);
        let tItems;
        //prepare items
        if (pItemCache != null) {
            tItems = this._getCompositeItems(pBasketId, pItemCache);
        }
        else {
            //get items and sub articles
            const tOptions = new basket.GetAllItemsOptions();
            tOptions.wholeComposite = true;
            tOptions.geometryIds = true;
            tOptions.setArticleIds = true;
            tItems = await this.mSession.basket.getAllItems([pBasketId], tOptions);
        }
        //get gfj url
        let tGfjUrl;
        if (isNullOrEmpty(pGfjUrl)) {
            try {
                tGfjUrl = await this.mSession.basket.getExportedGeometry(pBasketId, this.mGfjOptions);
            }
            catch (pError) {
                let tArticleDescr = pBasketId;
                if (tItems.length > 0)
                    tArticleDescr += " " + tItems[0].label;
                Log.error("getExportedGeometry failed for article " + tArticleDescr, pError);
            }
        }
        else {
            tGfjUrl = pGfjUrl;
        }
        if (!isNullOrEmpty(tGfjUrl)) {
            //load gfj
            const tResults = await this.mGfjLoader.load(tGfjUrl, pBasketId, tItems);
            if (tResults != null && tResults.length > 0) {
                return ({ node: tResults[0], items: tItems, gfjUrl: tGfjUrl });
            }
        }
        return ({ node: null, items: tItems });
    }
    /** @internal */
    _getCatalogImageFromCache(pImageId) {
        if (isNullOrEmpty(pImageId))
            return (null);
        return (this.mCatalogImageCache.get(pImageId));
    }
    /** @internal */
    _addCatalogImageToCache(pImageId, pImage) {
        if (pImage != null)
            this.mCatalogImageCache.add(pImageId, pImage);
    }
    /** @internal */
    _getCompositeItems(pMainId, pItemMap) {
        const tResult = new Array();
        const addSubArticles = (pItem) => {
            if (pItem.subArticleIds == null)
                return;
            for (const tSubId of pItem.subArticleIds) {
                const tSubItem = pItemMap.get(tSubId);
                if (tSubItem != null) {
                    tResult.push(tSubItem);
                    addSubArticles(tSubItem);
                }
                else {
                    Log.warn("Sub article not found: " + tSubId);
                }
            }
        };
        const tMainItem = pItemMap.get(pMainId);
        if (tMainItem != null) {
            tResult.push(tMainItem);
            addSubArticles(tMainItem);
        }
        else {
            Log.warn("Main article not found: " + pMainId);
        }
        return (tResult);
    }
    /** @internal */
    async _createArticleElement(pBasketId, pItemCache, pPropsCache, pGfjUrl) {
        if (isNullOrEmpty(pBasketId))
            throw new Error(ERR_INVALID_ARGUMENT);
        let tNode = null;
        let tItems = null;
        let tGfjUrl;
        const tResult = await this._importGfj(pBasketId, pItemCache, pGfjUrl);
        if (tResult != null) {
            tNode = tResult.node;
            tItems = tResult.items;
            tGfjUrl = tResult.gfjUrl;
        }
        const tArticle = new MainArticleElement(pBasketId, this, tNode);
        tArticle._setGfjUrl(tGfjUrl);
        if (tItems != null)
            tArticle.fillBasketItemCache(tItems);
        //fill props cache
        if (pPropsCache != null && tItems != null) {
            const tItemProps = new Array();
            for (const tItem of tItems) {
                const tProps = pPropsCache.get(tItem.itemId);
                if (tProps != null)
                    tItemProps.push(tProps);
            }
            tArticle.fillItemPropertiesCache(tItemProps);
        }
        if (tArticle.node == null) {
            await tArticle._initPlaceholderGeometry(tItems);
        }
        return (tArticle);
    }
    /** @internal */
    async _createUserArticleElement(pBasketId, pItemCache, pPropsCache) {
        if (isNullOrEmpty(pBasketId))
            throw new Error(ERR_INVALID_ARGUMENT);
        const tItems = this._getCompositeItems(pBasketId, pItemCache);
        const tArticle = new UserArticleElement(pBasketId, this, null);
        tArticle.fillBasketItemCache(tItems);
        //fill props cache
        if (pPropsCache != null) {
            const tProps = pPropsCache.get(pBasketId);
            if (tProps != null)
                tArticle.fillItemPropertiesCache([tProps]);
        }
        await tArticle._initPlaceholderGeometry(tItems);
        return (tArticle);
    }
    /** @internal */
    _GetDefaultItemPropsOptions() {
        const tPropsOptions = new basket.GetItemPropertiesOptions();
        tPropsOptions.separateCurrencies = true;
        tPropsOptions.inactivePositionState = true;
        tPropsOptions.includeCalculationErrorsInInconsistencies = this.includeCalculationErrorsInInconsistencies;
        return (tPropsOptions);
    }
    ensureArticlesAreInstanced(pArticles) {
        const tPromise = forEachParallel(pArticles, (pIndex, pArticle) => {
            return (pArticle.ensureIsInstanced());
        }, ArticleManager.MAX_CONCURRENT_TASKS);
        return (tPromise.then(() => { }));
    }
    saveArticleTransformations(pArticles) {
        const tPromise = forEachParallel(pArticles, (pIndex, pArticle) => {
            return (pArticle._saveTransformToEaiws());
        }, ArticleManager.MAX_CONCURRENT_TASKS);
        return (tPromise.then(() => { }));
    }
    deleteArticlesFromSession(pArticles) {
        if (pArticles == null || pArticles.length === 0)
            return (Promise.resolve());
        const tIds = new Array();
        const tPromise = forEachParallel(pArticles, (pIndex, pArticle) => {
            //prepare for recovery
            const tPromise = pArticle._prepareInstanceRecoveryData().then(() => {
                if (pArticle.basketId != null) {
                    tIds.push(pArticle.basketId);
                    pArticle._resetInstance();
                }
            });
            return (tPromise);
        }, ArticleManager.MAX_CONCURRENT_TASKS).then(() => {
            //delete the articles from the session
            return (this.mSession.basket.deleteItems(tIds));
        });
        return (tPromise);
    }
    collectMainArticlesInGroup(pGroup, pArticles) {
        if (pGroup == null)
            return;
        for (const tElement of pGroup.childElements) {
            if (tElement instanceof MainArticleElement)
                pArticles.push(tElement);
            if (tElement instanceof GroupElement)
                this.collectMainArticlesInGroup(tElement, pArticles);
        }
    }
}
//// internal //////////////////////////////////////////////////////////////////////////////
/** @internal */
ArticleManager.MAX_CONCURRENT_TASKS = 10;
